import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Eye, X, FileText } from 'lucide-react';
import { authedMediaUrl } from '../utils/media';
import { nameInitials, nameAvatarClasses } from '../utils/tagColors';

// Module-level "closer" for whichever hover preview is currently open, so opening a new one closes
// the previous (only one preview visible at a time across the app).
let CLOSE_ACTIVE_PREVIEW = null;

// Position a fixed popover next to an anchor rect: prefer the right, flip left if it would overflow,
// clamp vertically into the viewport.
const computePos = (rect, W, H) => {
  const GAP = 8;
  let left = rect.right + GAP;
  if (left + W > window.innerWidth - GAP) left = rect.left - W - GAP;
  if (left < GAP) left = GAP;
  let top = rect.top - 8;
  if (top + H > window.innerHeight - GAP) top = window.innerHeight - H - GAP;
  if (top < GAP) top = GAP;
  return { left, top };
};

// A file link that shows the file itself while the cursor is on it. Hovering opens a large bubble
// beside the link; moving the cursor away closes it after a short grace period, so crossing the gap
// into the bubble to look closer does not dismiss it. Clicking the link opens the file in a new tab.
//
// The trigger is whatever you pass as children, because the same behaviour is wanted behind a small
// eye icon in a form and behind a full attachment row on a receipt. A document is unreadable at
// thumbnail size, so the bubble is as large as the viewport allows up to 560x480.
//
// `url` is the path authedMediaUrl will sign. Pass `isPdf` when the type is known: these URLs end
// in /file rather than an extension, so it cannot be read off the path.
export const FileHoverPreview = ({
  url, label, children, isPdf: isPdfProp, className = 'inline-flex',
  // How big the bubble may get. The default suits an identity card, which is landscape and wide
  // rather than tall. A full invoice is portrait and its line items are unreadable in that shape,
  // so the receipt screen asks for a taller one.
  maxWidth = 560, maxHeight = 480,
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [imgOk, setImgOk] = useState(true);
  const anchorRef = useRef(null);
  const bubbleRef = useRef(null);
  const closeTimerRef = useRef(null);
  const href = url ? authedMediaUrl(url) : null;
  const isPdf = isPdfProp !== undefined ? isPdfProp : (url || '').toLowerCase().includes('.pdf');

  const cancelClose = () => { if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; } };
  const scheduleClose = () => { cancelClose(); closeTimerRef.current = setTimeout(() => setOpen(false), 120); };

  const openPreview = () => {
    cancelClose();
    if (!href || !anchorRef.current) return;
    if (CLOSE_ACTIVE_PREVIEW) CLOSE_ACTIVE_PREVIEW();
    CLOSE_ACTIVE_PREVIEW = () => setOpen(false);
    // As large as asked for, or as large as the viewport allows.
    const W = Math.min(maxWidth, window.innerWidth - 24);
    const H = Math.min(maxHeight, window.innerHeight - 24);
    setPos({ ...computePos(anchorRef.current.getBoundingClientRect(), W, H), width: W, height: H });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (anchorRef.current?.contains(e.target)) return; if (bubbleRef.current?.contains(e.target)) return; setOpen(false); };
    // Capture phase, stopped: Escape closes the preview and not a dialog it was opened from.
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true); window.removeEventListener('scroll', onScroll, true); };
  }, [open]);
  useEffect(() => cancelClose, []);

  if (!href) return null;

  return (
    <span ref={anchorRef} className={className} onMouseLeave={scheduleClose} onMouseEnter={openPreview}>
      {/* A render prop, given the signed link. Plain children are shown as they are rather than
          called: the expense screens once passed them that way, and every entry with a bill photo
          took the whole page down with "is not a function". */}
      {typeof children === 'function' ? children({ href, openPreview }) : children}
      {open && pos && createPortal(
        <div
          ref={bubbleRef}
          style={{ position: 'fixed', left: pos.left, top: pos.top, width: pos.width, zIndex: 70 }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-3"
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white border border-gray-200 shadow flex items-center justify-center text-gray-500 hover:text-red-500"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
          {isPdf || !imgOk ? (
            <div className="h-64 flex flex-col items-center justify-center text-gray-500 bg-gray-50 rounded-xl">
              <FileText className="h-12 w-12 mb-2" />
              <span className="text-sm">Click to open {isPdf ? 'PDF' : 'file'}</span>
            </div>
          ) : (
            <img
              src={href}
              alt={label}
              onError={() => setImgOk(false)}
              style={{ height: pos.height - 66 }}
              className="w-full object-contain rounded-xl bg-gray-50"
            />
          )}
          <div className="mt-2 text-sm font-semibold text-gray-800 text-center">{label}</div>
        </div>,
        document.body,
      )}
    </span>
  );
};

// The same preview behind a small eye link, for CNIC and other document fields on a form.
export const FilePreviewEye = ({ url, label }) => (
  <FileHoverPreview url={url} label={label}>
    {({ href }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700"
        title={`View ${label} - opens in a new tab`}
      >
        <Eye className="h-3.5 w-3.5" /> {label}
      </a>
    )}
  </FileHoverPreview>
);

// Avatar that shows a profile photo with a hover-preview popover, falling back to colour-hashed
// initials. Used in list rows (patients, users). `imageUrl` is the stored path.
export const AvatarHoverPreview = ({ imageUrl, name, size = 'w-10 h-10', rounded = 'rounded-full', textSize = 'text-sm' }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const anchorRef = useRef(null);
  const bubbleRef = useRef(null);
  const closeTimerRef = useRef(null);
  const imgUrl = imageUrl ? authedMediaUrl(imageUrl) : null;

  const cancelClose = () => { if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; } };
  const scheduleClose = () => { cancelClose(); closeTimerRef.current = setTimeout(() => setOpen(false), 120); };

  const openPreview = () => {
    cancelClose();
    if (!imgUrl || !anchorRef.current) return;
    if (CLOSE_ACTIVE_PREVIEW) CLOSE_ACTIVE_PREVIEW();
    CLOSE_ACTIVE_PREVIEW = () => setOpen(false);
    // Large preview, portrait-oriented for profile photos; shrinks on small viewports.
    const W = Math.min(380, window.innerWidth - 24);
    const H = Math.min(440, window.innerHeight - 24);
    setPos({ ...computePos(anchorRef.current.getBoundingClientRect(), W, H), width: W, height: H });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (anchorRef.current?.contains(e.target)) return; if (bubbleRef.current?.contains(e.target)) return; setOpen(false); };
    // Capture phase, stopped: Escape closes the preview and not a dialog it was opened from.
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true); window.removeEventListener('scroll', onScroll, true); };
  }, [open]);
  useEffect(() => cancelClose, []);

  return (
    <div ref={anchorRef} className="flex-shrink-0" onClick={(e) => e.stopPropagation()} onMouseLeave={scheduleClose}>
      {imgUrl ? (
        <img
          src={imgUrl}
          alt={name}
          onMouseEnter={openPreview}
          className={`${size} ${rounded} object-cover border border-gray-200 cursor-pointer`}
        />
      ) : (
        <div className={`${size} ${rounded} flex items-center justify-center ${textSize} font-bold border ${nameAvatarClasses(name)}`}>
          {nameInitials(name)}
        </div>
      )}
      {open && imgUrl && pos && createPortal(
        <div
          ref={bubbleRef}
          style={{ position: 'fixed', left: pos.left, top: pos.top, width: pos.width, zIndex: 70 }}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-3"
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white border border-gray-200 shadow flex items-center justify-center text-gray-500 hover:text-red-500"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <img
            src={imgUrl}
            alt={name}
            style={{ height: pos.height - 66 }}
            className="w-full object-contain rounded-xl bg-gray-50"
          />
          <div className="mt-2 text-sm font-semibold text-gray-800 truncate text-center">{name}</div>
        </div>,
        document.body,
      )}
    </div>
  );
};
